import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { Hotel, RoomClass } from 'ts/hotel/interfaces/HotelDetails';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { checkAHAVI, highlightStr, replaceHtmlToStr } from 'utils/utils';
import { formatPriceHotel, getHotelDisplaySettings, getKey, HotelDisplaySettings, saveHotelDisplaySettings } from 'utils/hotel';
import ProposalHeart from 'components/shared/Proposal/Create/ProposalHeart';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { faBed, faCircleArrowRight } from '@fortawesome/free-solid-svg-icons';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { useTranslation } from 'react-i18next';
import CancellationPolicyModal from '../Result/Modals/CancellationPolicyModal';
import { ListOfRoomInfo } from 'ts/hotel/interfaces/HotelInfoResponse';
import { getHotelInfo } from 'services/hotel';
import { RoomDetailsPolling } from './DetailsSkeleton';
import Slider from 'react-slick';
import { useLocation, useSearchParams } from 'react-router-dom';
import RoomsFilter from './RoomsFilter';
import { FilterRoom } from 'ts/hotel/classes/FilterRoom';
import RoomDetailsSlider from './RoomDetailsSlider';
import RoomIcons from '../Result/Cards/RoomIcons';
import { useHotelProposal } from 'hooks/useProposal';
import { MainContext } from 'contexts/MainContext';
import { HotelConfigContext } from 'contexts/HotelConfigContext';

interface RoomDetailsProps {
  rooms: RoomClass[];
  setRooms: (a: RoomClass[]) => void;
  hotel: Hotel;
  poolingFinished: boolean;
  onClickBook: (room: RoomClass) => void;
  bookLoading: boolean;
}

const RoomDetails: FC<RoomDetailsProps> = ({ rooms, ...props }) => {
  const [t] = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { hotelIndex } = useContext(HotelConfigContext);
  const { user, userSiteConfig } = useContext(MainContext);
  const [cancellationModalVisibleIndex, setCancellationModalVisibleIndex] = useState(-1);
  const [choosenRoomInfo, setChoosenRoomInfo] = useState<ListOfRoomInfo>();
  const [filterRoom, setFilterRoom] = useState<FilterRoom>();
  const [remarkModalVisibleIndex, setRemarkModalVisibleIndex] = useState(-1);
  const [choosenRoom, setChoosenRoom] = useState<RoomClass>();
  const [nav1, setNav1] = useState<Slider | null>(null);
  const [nav2, setNav2] = useState<Slider | null>(null);
  const slider1 = useRef<Slider>(null);
  const slider2 = useRef<Slider>(null);
  const [rerendered, setRerendered] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const roomRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const { isRoomInProposal, toggleRoomProposal } = useHotelProposal();

  const dsettings = getHotelDisplaySettings(hotelIndex);
  const [displaySettings, setDisplaySettings] = useState(dsettings);
  const { priceMode, currency } = displaySettings;

  useEffect(() => {
    if (rooms.length) {
      const sortedRooms = [...rooms].sort((a, b) => {
        const aIsUrl = isRoomAsUrl(a);
        const bIsUrl = isRoomAsUrl(b);
        if (aIsUrl && !bIsUrl) return -1;
        if (!aIsUrl && bIsUrl) return 1;
        return 0;
      });

      const hasChanged = sortedRooms.some(
        (room, index) => room.rcUniqueKey !== rooms[index].rcUniqueKey
      );
      if (hasChanged) {
        props.setRooms(sortedRooms);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms]);

  useEffect(() => {
    if (rooms.length && !choosenRoomInfo) {
      const roomsUrl = rooms.find(isRoomAsUrl);
      if (roomsUrl) {
        onCheckRoom(roomsUrl);
        setChoosenRoom(rooms.find(isRoomAsUrl));
      } else if (props.poolingFinished) {
        onCheckRoom(rooms[0]);
        setChoosenRoom(rooms[0]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;

      const visibleRooms = rooms.filter((room) => room.visible && room.visibleSm);
      if (visibleRooms.length === 0) return;

      const currentIndex = choosenRoom
        ? visibleRooms.findIndex((room) => room.rcUniqueKey === choosenRoom.rcUniqueKey)
        : -1;

      let nextIndex: number;

      if (e.key === 'ArrowDown') {
        if (currentIndex >= visibleRooms.length - 1) return;
        nextIndex = currentIndex + 1;
      } else {
        if (currentIndex <= 0) return;
        nextIndex = currentIndex - 1;
      }

      e.preventDefault();

      const nextRoom = visibleRooms[nextIndex];
      setChoosenRoom(nextRoom);
      onCheckRoom(nextRoom);

      const roomElement = roomRefs.current[nextRoom.rcUniqueKey];
      if (roomElement) {
        roomElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, choosenRoom]);

  const getSuppText = (room: RoomClass) => {
    if (!user?.branch?.hotelDisplaySupplierName) return '';

    if (room.suppDesc) {
      return room.suppDesc;
    }
  };

  const getCxlStyle = (room: RoomClass) => {
    const isPastCxl =
      new Date(room.cxl.limitDate) < new Date() &&
      new Date(room.cxl.limitDate) > new Date(2000, 1, 1);
    return { color: isPastCxl ? 'var(--color-danger)' : '' };
  };

  const onCheckRoom = async (room: RoomClass) => {
    setChoosenRoomInfo(undefined);
    const searchToken = sessionStorage.getItem(getKey('searchTokenRooms', hotelIndex)) || '';

    const res = await getHotelInfo(
      {
        searchToken,
        hotelUkey: room.htuKey,
        roomBToken: room.hotelRooms[0].bToken,
        afterOneHotelSearch: true,
      },
      hotelIndex
    );

    const choosenRoom = res?.hotelDetails?.listOfRoomInfo?.find((roomInfo) =>
      room.desc?.includes(roomInfo.shortDesc)
    );

    setChoosenRoomInfo(choosenRoom);
    setRerendered(true);

    setTimeout(() => {
      setRerendered(false);
    }, 100);

    setTimeout(() => {
      setNav1(slider1.current);
      setNav2(slider2.current);
    }, 300);
  };

  const isRoomAsUrl = (rc: RoomClass) => {
    const detailsStrParams = searchParams.get('details');
    const detailsObjParams = new URLSearchParams(detailsStrParams || '');

    const { roomPrice, roomName, roomCxl, roomSupp } = location.pathname.includes('results')
      ? Object.fromEntries(detailsObjParams)
      : Object.fromEntries(searchParams);

    const result =
      rc.price.usd + '' === roomPrice &&
      rc.displayName.includes(roomName) &&
      rc.suppCode === roomSupp &&
      rc.cxl.limitDate?.split('T')[0] === roomCxl;

    return result;
  };

  const onChangeDisplaySettings = (settings: HotelDisplaySettings) => {
    setDisplaySettings(settings);
    saveHotelDisplaySettings(settings, hotelIndex);
  };
  const renderImageSupplement = (room: RoomClass) => {
    const hasImageSupplement = rooms
      .filter((room) => room.visible && room.visibleSm)
      .some((room) => room.hotelRooms[0].suppliments?.filter((sup) => sup.type === 'I')?.[0]?.desc);

    if (!hasImageSupplement || window.innerWidth < 768) return null;
    const url = room.hotelRooms[0].suppliments?.filter((sup) => sup.type === 'I')?.[0]?.desc;

    const onSelectRoom = () => {
      setChoosenRoom(room);
      onCheckRoom(room);
    };

    return (
      <div style={{ width: 80 }}>
        {!!url && !imageErrors[room.rcUniqueKey] ? (
          <img
            src={url}
            alt="1"
            style={{ width: 70, height: 40, borderRadius: 5, cursor: 'pointer' }}
            onClick={onSelectRoom}
            onError={() =>
              setImageErrors((prev) => ({ ...prev, [room.rcUniqueKey]: true }))
            }
          />
        ) : (
          <div className="flex-center" style={{ width: 80, height: 40 }} onClick={onSelectRoom}>
            <FontAwesomeIcon icon={faBed} style={{ width: 50, height: 40, cursor: 'pointer' }} />
          </div>
        )}
      </div>
    );
  };

  return (
    <Fragment>
      <div className="position-relative">
        <div className="details-block default-shadow">
          <div className="title title-header-grey" style={{ marginBottom: 10 }}>
            <span>
              {t('hotel.select_room')} <span className="text-light">({rooms.length})</span>
            </span>
          </div>

          <div className="hotel-card-rooms-ungroup position-relative" dir="ltr">
            <RoomsFilter
              rooms={rooms}
              setRooms={props.setRooms}
              setFilterRoom={setFilterRoom}
              hotel={props.hotel}
              bigMode={true}
              displaySettings={displaySettings}
              onChangeDisplaySettings={onChangeDisplaySettings}
            />
            <div className="hotel-card-rooms">
              {rooms
                .filter((room) => room.visibleSm && room.visible)
                .map((room, index) => {
                  return (
                    <div
                      ref={(el) => (roomRefs.current[room.rcUniqueKey] = el)}
                      className={
                        'hotel-card-room ' +
                        (new Date(room.cxl.limitDate) < new Date() ? 'room-cxl' : '') +
                        (isRoomAsUrl(room) ? ' room-as-url' : '') +
                        (choosenRoom?.rcUniqueKey === room.rcUniqueKey ? ' room-active' : '')
                      }
                      key={index}
                    >
                      {renderImageSupplement(room)}
                      <div className="flex-one">
                        <div className="flex-row text-bold text-dark">
                          <div className="flex-one">
                            <div className="display-flex">
                              <div>
                                {room.hotelRooms.map((hotelRoom, hrIndex) => (
                                  <div key={hrIndex}>
                                    <span
                                      className="cur-poi text-lh-sm"
                                      dangerouslySetInnerHTML={{
                                        __html: highlightStr(
                                          userSiteConfig.hotel.isRoomNameCleared
                                            ? hotelRoom.displayNameCleared || hotelRoom.displayName
                                            : hotelRoom.displayName,
                                          filterRoom?.name || ''
                                        ),
                                      }}
                                      onClick={() => {
                                        setChoosenRoom(room);
                                        onCheckRoom(room);
                                      }}
                                    ></span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className='flex-row text-normal'>
                            <RoomIcons
                              boardCode={room.board.basis.code}
                              limitDate={room.cxl.limitDate}
                              ada={!!room.hotelRooms[0].ada}
                              hasRemarks={!!(room.remarks || room.remCondition)}
                              onCancellationClick={() =>
                                user?.branch?.hotelDisplaySupplierName &&
                                setCancellationModalVisibleIndex(index)
                              }
                              onRemarksClick={() => setRemarkModalVisibleIndex(index)}
                            />

                          </div>
                          <div
                            className="room-price price-hover-target cur-poi"
                            onClick={() => props.onClickBook(room)}
                            style={{ flex: '0 0 60px', textAlign: 'right' }}
                          >
                            <Tooltip
                              content={formatPriceHotel(room.price, currency, priceMode).replace(
                                /<[^>]*>/g,
                                ''
                              )}
                              position="top"
                            >
                              <span className="price-hover-swap">
                                <span className="price-hover-values">
                                  {user?.branch?.hotelDisplaySupplierName && (
                                    <span
                                      className="text-light mr-5 text-normal"
                                      style={{ position: 'absolute', right: -5, top: 27 }}
                                    >
                                      <span
                                        dangerouslySetInnerHTML={{
                                          __html: formatPriceHotel(
                                            room.price,
                                            room.price.currency,
                                            priceMode
                                          ),
                                        }}
                                      />
                                    </span>
                                  )}

                                  <span style={getCxlStyle(room)}>
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: formatPriceHotel(room.price, currency, priceMode),
                                      }}
                                    />
                                  </span>
                                </span>
                                <span className="price-hover-arrow" aria-hidden>
                                  <FontAwesomeIcon icon={faCircleArrowRight} />
                                </span>
                              </span>
                            </Tooltip>
                          </div>
                        </div>

                        <div className="flex-row">
                          <div className="text-light flex-one flex-col-end flex-row">
                            <ProposalHeart
                              active={!!isRoomInProposal(props.hotel, room as any)}
                              onClickPropsal={(e) => {
                                e.stopPropagation();
                                toggleRoomProposal(props.hotel, room as any);
                              }}
                              isEnvelope={true}
                            />
                            {getSuppText(room)}
                            {isRoomAsUrl(room) && (
                              <span className="ph-5">({t('hotel.original_room_description')})</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="room-actions"></div>
                      {cancellationModalVisibleIndex === index && (
                        <CancellationPolicyModal
                          hotel={props.hotel as any}
                          onClose={() => setCancellationModalVisibleIndex(-1)}
                          rc={room as any}
                          htuKey={props.hotel.htuKey}
                        />
                      )}

                      {remarkModalVisibleIndex === index && (
                        <CancellationPolicyModal
                          hotel={props.hotel as any}
                          onClose={() => setRemarkModalVisibleIndex(-1)}
                          rc={room as any}
                          isRemark={true}
                        />
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
          {!props.poolingFinished && <RoomDetailsPolling noChoosenRoom={false} />}
        </div>
      </div>

      {(!!choosenRoomInfo ||
        !!choosenRoom?.hotelRooms[0].suppliments?.some((sup) => sup.type === 'I')) && (
          <div className="details-block default-shadow">
            <div className="title-underline text-lg mb-20 title-header-grey" dir="ltr">
              {choosenRoom?.hotelRooms[0].displayName}
            </div>
            {!rerendered && (
              <RoomDetailsSlider
                choosenRoom={choosenRoom}
                nav1={nav1}
                nav2={nav2}
                slider1={slider1}
                slider2={slider2}
              />
            )}

            {!!choosenRoomInfo && (
              <Fragment>
                {choosenRoomInfo.desc.length && (
                  <div
                    className="mb-20 text-lh"
                    dir={checkAHAVI(choosenRoomInfo.desc) ? 'rtl' : 'ltr'}
                  >
                    <div className="title text-md">{t('hotel.about_room')}</div>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: replaceHtmlToStr(choosenRoomInfo.desc),
                      }}
                    ></div>
                  </div>
                )}
                {choosenRoomInfo.listOfAmenity?.length && (
                  <div
                    className="row mb-20"
                    dir={
                      choosenRoomInfo.listOfAmenity?.some((amenity) => checkAHAVI(amenity.desc))
                        ? 'rtl'
                        : 'ltr'
                    }
                  >
                    <div className="col-12 title text-md">{t('hotel.room_facilities')}</div>
                    {choosenRoomInfo.listOfAmenity.map((amenity, aIndex) => (
                      <div className="col-md-6 col-12 mb-5" key={aIndex}>
                        <Checkbox label={amenity.desc} disabled checked={true} />
                      </div>
                    ))}
                  </div>
                )}
              </Fragment>
            )}
          </div>
        )}
    </Fragment>
  );
};

export default withErrorBoundary(RoomDetails, 'RoomDetails');
